import { useEffect, useRef, useState } from 'react'
import { useI18n } from '@/i18n/I18nProvider'
import {
  TURNSTILE_ENABLED,
  loadTurnstile,
  submitTool,
  type SubmitState,
  type ToolResult,
} from './submit'
import { TURNSTILE_SITE_KEY } from '@/lib/site'

/**
 * The three fields between finishing a tool and seeing the result.
 *
 * Kagan, 2026-08-25: "hepsi lead yakalamalı" — so all three tools gate their
 * report, the Guts Check included, which had no capture at all in the source
 * HTML.
 *
 * ---------------------------------------------------------------------------
 * THE ORDERING DECISION, which is the only interesting thing here.
 *
 * The gate comes AFTER the questions and BEFORE the report, not before the
 * questions. Asking for an email to begin is how you lose the nine people in
 * ten who would have answered honestly and then given you their address because
 * they wanted the result. Asking after means the work is already done and the
 * exchange is obvious: you answered twelve questions, here is what they say,
 * tell us where to send it.
 *
 * And the result is NOT withheld if the submit fails. A network error must not
 * cost somebody the twelve answers they just gave — they see their verdict
 * either way, with a line saying we could not save it. The lead is our problem;
 * the report is theirs.
 * ---------------------------------------------------------------------------
 */
export default function LeadGate({
  tool,
  result,
  openedAt,
  onDone,
}: {
  tool: string
  result: ToolResult
  /** When the tool mounted — the Worker's elapsed-time floor measures from here. */
  openedAt: number
  /** Called once the gate is passed, whether or not the save succeeded. */
  onDone: (state: SubmitState) => void
}) {
  const { locale, t } = useI18n()
  const copy = t.tools.gate

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [consent, setConsent] = useState(false)
  const [state, setState] = useState<SubmitState>({ status: 'idle' })
  /** The honeypot. Rendered, off-screen, and always empty for a real person. */
  const [honeypot, setHoneypot] = useState('')

  const widget = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)
  const [challengeReady, setChallengeReady] = useState(!TURNSTILE_ENABLED)
  /** Turnstile errored or never loaded — a send without a token will be refused. */
  const [challengeFailed, setChallengeFailed] = useState(false)

  /**
   * Put the form on screen when it replaces the questions.
   *
   * Kagan, 01.09.2026: "toollarda testi bitirince form dolduramıyoruz". The
   * form was there and working — it was simply not in the viewport. The last
   * question is a tall block and its button sits at the bottom of it; swapping
   * that block for this short card shortens the page by several hundred pixels
   * without moving the scroll position, so the visitor is left looking at the
   * prose *below* the tool and concludes the tool ended. On /tools, which
   * carries three tools on one page, they are looking at the next tool's intro
   * instead, which is worse.
   *
   * `block: 'center'` rather than `'start'`, for the same reason as
   * ContactForm's thanks panel: the header is sticky, and a card aligned to the
   * top of the viewport starts underneath it.
   */
  const card = useRef<HTMLDivElement>(null)
  useEffect(() => {
    card.current?.scrollIntoView({ block: 'center' })
  }, [])

  useEffect(() => {
    if (!TURNSTILE_ENABLED) return
    let cancelled = false
    loadTurnstile()
      .then(() => {
        if (cancelled || !widget.current || !window.turnstile) return
        // Rendered once; `widgetId` guards against a re-render mounting a second.
        if (widgetId.current) return
        widgetId.current = window.turnstile.render(widget.current, {
          sitekey: TURNSTILE_SITE_KEY,
          // RETRY IS SAID OUT LOUD (2026-10-02). 'auto' is Turnstile's default,
          // so this changes nothing today; it is written so a later edit cannot
          // turn it off unnoticed — `verify-turnstile.mjs` requires it at every
          // render. Do NOT add a `reset()` to the error-callback as well: the
          // widget is already retrying, and a reset would restart it mid-retry.
          retry: 'auto',
          // Invisible until it needs to challenge, which is the point of
          // Turnstile over a checkbox: most visitors never see anything.
          appearance: 'interaction-only',
          callback: () => {
            setChallengeFailed(false)
            setChallengeReady(true)
          },
          // Still lets the reader through — the tools show their report either
          // way and the Worker decides what a missing token means — but the
          // reason is no longer thrown away. This callback was an empty
          // function while the site key carried a typo, so Turnstile's
          // `400020 — invalid sitekey` sat in the console unread for a week
          // (see TURNSTILE_SITE_KEY).
          'error-callback': (code?: string) => {
            console.error('[dp] turnstile refused to render:', code)
            setChallengeFailed(true)
            setChallengeReady(true)
          },
        })
      })
      // A blocked or failed third-party script must not brick the form. The
      // Worker decides what a missing token means; here we simply stop waiting.
      .catch(() => {
        setChallengeFailed(true)
        setChallengeReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * What is still missing, in the order the fields are read.
   *
   * The button used to be `disabled` until all of this was true, which answers a
   * click with nothing at all — indistinguishable from a broken button, and the
   * consent box is easy to miss under three text fields. It is enabled now, and
   * pressing it early lists what is left rather than doing nothing. Derived on
   * every render rather than captured on the click, so each line disappears the
   * moment that field is put right.
   */
  const problems = [
    fullName.trim().length === 0 && copy.missing.name,
    !/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email.trim()) && copy.missing.email,
    // COMPANY WAS MISSING FROM THIS LIST while the Worker required it and
    // `copy.missing.company` had been written for exactly this line. So the
    // form cheerfully submitted without it and the save was refused —
    // 2026-09-09, found by Fatih walking the ROI calculator step by step. A
    // required field enforced on one side only is worse than one enforced on
    // neither: the visitor is told nothing and we lose the lead.
    company.trim().length === 0 && copy.missing.company,
    !consent && copy.missing.consent,
    !challengeReady && copy.missing.challenge,
  ].filter((line): line is string => typeof line === 'string')

  const [showProblems, setShowProblems] = useState(false)

  const canSend = state.status !== 'sending' && problems.length === 0

  async function send() {
    if (problems.length > 0) {
      setShowProblems(true)
      return
    }
    if (!canSend) return
    const token = TURNSTILE_ENABLED
      ? window.turnstile?.getResponse(widgetId.current ?? undefined)
      : undefined
    // The Worker refuses a tool lead with no token, so a widget that failed
    // would only earn the reader a refusal they cannot act on. Say why here, in
    // their language, with an address — and keep them on the form, because
    // Turnstile's own retry may still produce a token and a second press then works.
    if (TURNSTILE_ENABLED && !token && challengeFailed) {
      setState({ status: 'failed', message: copy.challengeFailed, fixable: true })
      return
    }
    setState({ status: 'sending' })
    const next = await submitTool(
      tool,
      locale,
      { full_name: fullName.trim(), email: email.trim(), company: company.trim(), consent },
      result,
      { openedAt, honeypot, turnstileToken: token },
    )
    setState(next)
    // A FIXABLE REFUSAL KEEPS THEM HERE. The header's rule — a failed save must
    // not withhold the result — was written about network errors and quietly
    // swallowed the 4xx case with them, so somebody who had merely left a field
    // blank was carried off to the report with a message saying we could not
    // save it. They can put it right in two seconds if we let them see it.
    // A Turnstile token is single-use, but only the challenge refusal spends
    // it — the Worker checks every field before it gets there. So this is
    // conditional now: resetting after "you forgot your company" would make
    // somebody solve a challenge again for a mistake that never reached one.
    // A non-fixable failure (5xx, a dead relay) resets too, since the token
    // there really may have been spent upstream.
    if (
      next.status === 'failed' &&
      TURNSTILE_ENABLED &&
      (next.staleChallenge || !next.fixable)
    ) {
      window.turnstile?.reset(widgetId.current ?? undefined)
      setChallengeReady(false)
    }
    if (next.status === 'failed' && next.fixable) return
    if (next.status === 'sent' || next.status === 'failed') onDone(next)
  }

  const field =
    'mt-2 w-full rounded-lg border border-border bg-background px-4 py-3 text-base ' +
    'text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none'

  return (
    <div
      ref={card}
      className="mx-auto max-w-xl rounded-2xl border border-border bg-background p-8"
    >
      <h3 className="text-2xl font-bold tracking-tight text-foreground">{copy.heading}</h3>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground">{copy.sub}</p>

      <div className="mt-6 space-y-4">
        <label className="block">
          <span className="text-sm font-semibold text-foreground">{copy.name}</span>
          <input
            className={field}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
            placeholder={copy.namePlaceholder}
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-foreground">{copy.email}</span>
          <input
            className={field}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder={copy.emailPlaceholder}
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-foreground">{copy.company}</span>
          <input
            className={field}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            autoComplete="organization"
            placeholder={copy.companyPlaceholder}
          />
        </label>

        {/*
          The honeypot. Off-screen rather than `display:none`, because some bots
          skip hidden inputs but fill anything they can find in the DOM.
          `tabIndex={-1}` and `aria-hidden` keep it away from keyboard and
          screen-reader users, who are the people a `display:none` trick
          actually inconveniences.
        */}
        <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden>
          <label>
            Website
            <input
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </label>
        </div>

        <label className="flex items-start gap-3 pt-2">
          <input
            type="checkbox"
            className="mt-1 size-4 flex-none"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span className="text-sm leading-relaxed text-muted-foreground">{copy.consent}</span>
        </label>

        <div ref={widget} className="empty:hidden" />

        <button
          className="btn-primary w-full"
          onClick={send}
          disabled={state.status === 'sending'}
        >
          {state.status === 'sending' ? copy.sending : copy.submit}
        </button>

        {showProblems && problems.length > 0 && (
          <div role="alert" className="text-sm leading-relaxed text-destructive">
            <p className="font-medium">{copy.missing.heading}</p>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {problems.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}

        {state.status === 'failed' && (
          <p className="text-sm font-medium text-[var(--status-ongoing-fg)]">{state.message}</p>
        )}

        <p className="text-xs leading-relaxed text-muted-foreground">{copy.privacy}</p>
      </div>
    </div>
  )
}
